import { Component, ChangeDetectionStrategy, OnInit, signal, computed, inject, effect, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { IrisFilesService, IrisFilePreviewType } from '@iris/common/modules/dms/services/files.service';
import { Router } from '@angular/router';
import { IrisDMSFileI } from '@iris/common/modules/dms/models/IrisDMSFile';
import { IrisDMSFileQueryParam, IrisDMSFileQueryParamAction } from '@iris/common/modules/dms/models/IrisDMSFileQueryParam';
import { goToPage } from '@iris/common/utils/router.utils';
import { PdfLoadedEvent, pdfDefaultOptions } from 'ngx-extended-pdf-viewer';

import { distinctUntilChanged, filter, switchMap, take } from 'rxjs';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { IrisDmsRoutingService } from '@iris/common/modules/dms/services/dms-routing.service';
import { IrisGlobalSandbox } from '@iris/common/redux/global.sandbox';
import { tap } from 'rxjs/operators';
import { IrisFoldersService } from '@iris/common/services/docs/folders.service';
import { DOCUMENT } from '@angular/common';
import { downloadFileFromDataUrl, isDataUrl } from '@iris/common/utils/file.utils';
import { SkModalBase } from '@ngx-strakit/cdk';

const OUT_OF_MEMORY_ERROR_TEXT = 'Out of memory';
const INVALID_STRUCTURE_ERROR_TEXT = 'Invalid PDF structure';
const UNEXPECTED_RESPONSE = 'Unexpected server response';

export interface FilesPreviewModalDataI {
  filesList: IrisDataRepoFileI[] | IrisDMSFileI[];
  file: IrisDataRepoFileI;
  openDms: boolean;
  showGoToFolder: boolean;
  showFileInfo: boolean;
  saveRecentFile: boolean;
}

@Component({
  selector: 'files-preview-modal',
  templateUrl: './files-preview-modal.component.html',
  styleUrls: ['./files-preview-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FilesPreviewModalComponent extends SkModalBase<FilesPreviewModalComponent, void> implements OnInit, OnDestroy {
  private readonly filesService = inject(IrisFilesService);
  private readonly foldersService = inject(IrisFoldersService);
  private readonly dmsRoutingService = inject(IrisDmsRoutingService);
  private readonly router = inject(Router);
  private readonly globalSandbox = inject(IrisGlobalSandbox);
  private readonly document = inject<Document>(DOCUMENT);
  readonly data = inject<FilesPreviewModalDataI>(MAT_DIALOG_DATA);

  set filesList(filesList: IrisDataRepoFileI[] | IrisDMSFileI[]) {
    if (!!filesList && filesList.length > 0 && !!this.file) {
      this._filesList = filesList;
      this.currentIndex.set(this.filesList.findIndex(({ id }) => id == this.file.id));
    }

    if (this.currentIndex() === null || this.currentIndex() === -1) {
      this.currentIndex.set(0);
      this._filesList = [this.file];
    }
  }

  get filesList(): IrisDataRepoFileI[] | IrisDMSFileI[] {
    return this._filesList;
  }

  readonly file = this.data.file;
  readonly currentIndex = signal<number>(null);
  readonly showGoToFolder = this.data.showGoToFolder;
  readonly showFileInfo = this.data.showFileInfo;
  readonly previewReady = signal(false);
  sidebarVisible: boolean;
  readonly isError = signal(false);

  private readonly currentFile = computed(() => {
    const currentFile = this.filesList[this.currentIndex()];
    if (currentFile.id === this.file.id) {
      return this.file;
    }
    return currentFile;
  });
  private readonly currentFileId = computed(() => this.currentFile().id);
  readonly fileName = computed(() => this.currentFile().name);
  readonly currentFileMimeType = computed(() => this.currentFile().mimeType);
  readonly fileLink = computed(() => {
    if (this.file.id === this.currentFileId() && !!this.file.fileLink) {
      return this.file.fileLink;
    }
    return this.filesService.getFilePreviewUrl(this.currentFileId(), false, this.data.saveRecentFile);
  });
  readonly fileLink$ = toObservable(this.fileLink);
  readonly fileInfoHref = computed(() => this.dmsRoutingService.getFileInternalLink(this.currentFile()?.id, false));

  readonly previewFileType = computed(() => this.filesService.getPreviewType(this.currentFile()));
  readonly isExcelFile = computed(() => this.filesService.isExcelFile(this.currentFileMimeType()));
  readonly isImage = computed(() => this.previewFileType() === IrisFilePreviewType.Image);
  readonly isPdf = computed(() => this.previewFileType() === IrisFilePreviewType.Pdf);
  readonly isDocument = computed(() => this.previewFileType() === IrisFilePreviewType.Document);

  private currentBlobUrl: string | null = null;

  private readonly documentBlob$ = this.fileLink$.pipe(
    filter(() => this.isDocument()),
    distinctUntilChanged(),
    switchMap(fileLink => this.filesService.getFileLinkBase64String$(fileLink)),
  );
  readonly documentBlob = toSignal(this.documentBlob$, { initialValue: '' });

  private prevAndNextDisabled: boolean;
  private _filesList: IrisDataRepoFileI[] | IrisDMSFileI[] = [];
  private readonly openDms = this.data.openDms;

  constructor() {
    super();
    pdfDefaultOptions.annotationEditorMode = -1;

    effect(() => {
      const newBlobUrl = this.documentBlob();
      if (this.currentBlobUrl && this.currentBlobUrl !== newBlobUrl) {
        URL.revokeObjectURL(this.currentBlobUrl);
      }
      this.currentBlobUrl = newBlobUrl;
    });
  }

  get listLength(): number {
    return this.filesList?.length ?? 0;
  }

  get prevButtonDisabled(): boolean {
    return this.prevAndNextDisabled || (this.isPdf() && this.sidebarVisible);
  }

  get nextButtonDisabled(): boolean {
    if (this.prevAndNextDisabled) { return true; }
    if (this.isPdf()) {
      const secondaryToolbar = this.document.getElementById('secondaryToolbar');
      const secondaryToolbarVisible = !(secondaryToolbar?.classList?.contains('hidden') ?? true);
      if (secondaryToolbarVisible) { return true; }
    }
    return false;
  }

  ngOnInit(): void {
    this.filesList = this.data.filesList;
    this.prevAndNextDisabled = this.listLength <= 1;
    this.changeURL();
  }

  onPdfLoaded(ev: PdfLoadedEvent): void {
    if (ev.pagesCount > 0) {
      this._showPreview();
    }
  }

  onImageLoaded(_ev: Event): void {
    this._showPreview();
  }

  onPdfLoadingFailed(ev: Error): void {
    const m = ev.message;
    if (m && [
      OUT_OF_MEMORY_ERROR_TEXT,
      INVALID_STRUCTURE_ERROR_TEXT,
      UNEXPECTED_RESPONSE,
    ].some((x) => m.includes(x))) {
      this._showError();
    }
  }

  onImageError(): void {
    this._showError();
  }

  nextFile(): void {
    let index = this.getNextIndex(this.currentIndex());
    while (this.filesService.isOfficeDocument(this.filesList[index].mimeType)) {
      index = this.getNextIndex(index);
    }
    this.currentIndex.set(index);
    this._showLoader();
    this.changeURL();
  }

  prevFile(): void {
    let index = this.getPreviousIndex(this.currentIndex());
    while (this.filesService.isOfficeDocument(this.filesList[index].mimeType)) {
      index = this.getPreviousIndex(index);
    }
    this.currentIndex.set(index);
    this._showLoader();
    this.changeURL();
  }

  changeURL(): void {
    if (!this.openDms) { return; }
    goToPage(this.router, [], {
      queryParams: { [IrisDMSFileQueryParam.File]: this.currentFileId() },
      queryParamsHandling: 'merge',
    });
  }

  goToParentFolder(): void {
    const parentFolderId = this.currentFile().parentId;

    this.foldersService.getFolderById(parentFolderId).pipe(
      tap(folder => {
        goToPage(
          this.router,
          [this.globalSandbox.appAlias(), 'dms', folder?.isSystem ? 'system-folders' : 'folders', parentFolderId, 'files'],
          { queryParams: { file: this.currentFileId(), action: IrisDMSFileQueryParamAction.Select } },
        );

        this.closeWithoutResult();
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  downloadFile(): void {
    if (this.file.fileLink && isDataUrl(this.file.fileLink)) {
      downloadFileFromDataUrl(this.file.fileLink, this.file.name);
      return;
    }
    this.filesService.downloadFile(this.currentFileId(), this.fileName()).pipe(
      take(1),
    ).subscribe();
  }

  openFileInfoInNewTab(): void {
    const url = this.fileInfoHref();
    if (!url) {
      return;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  }

  private _showPreview(): void {
    this.previewReady.set(true);
  }

  private _showLoader(): void {
    if (this.isPdf() || this.isImage()) {
      this.previewReady.set(false);
    }
  }

  private _showError(): void {
    this.isError.set(true);
    this.previewReady.set(true);
  }

  private getNextIndex(index: number): number {
    if (index === (this.listLength - 1)) {
      return 0;
    } else {
      // eslint-disable-next-line no-useless-assignment
      return ++index;
    }
  }

  private getPreviousIndex(index: number): number {
    if (index > 0) {
      // eslint-disable-next-line no-useless-assignment
      return --index;
    } else {
      return this.listLength - 1;
    }
  }

  ngOnDestroy(): void {
    if (this.currentBlobUrl) {
      URL.revokeObjectURL(this.currentBlobUrl);
    }
    super.ngOnDestroy();
  }
}
